import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Comparison Card Component
 * Displays a metric with current and previous period values, and change indicator
 */
const ComparisonCard = ({ 
  title, 
  currentValue, 
  previousValue, 
  format = 'number',
  icon: Icon,
  subtitle,
  className 
}) => {
  // Calculate change
  const change = previousValue !== 0 && previousValue !== null
    ? ((currentValue - previousValue) / previousValue) * 100
    : currentValue > 0 ? 100 : 0;
  
  const changeAbs = currentValue - previousValue;
  const isPositive = change > 0;
  const isNegative = change < 0;
  const isNeutral = change === 0;

  // Format value based on type
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    switch (format) {
      case 'currency':
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'percentage':
        return `${value.toFixed(2)}%`;
      case 'number':
      default:
        return value.toLocaleString();
    }
  };

  return (
    <Card className={cn('', className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 pt-2 px-2 sm:px-3">
        <CardTitle className="text-xs font-medium text-muted-foreground truncate pr-1">{title}</CardTitle>
        {Icon && <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground flex-shrink-0" />}
      </CardHeader>
      <CardContent className="px-2 sm:px-3 pb-2 pt-0">
        <div className="space-y-2">
          {/* Current Period */}
          <div>
            <div className="text-base sm:text-lg lg:text-xl font-bold">{formatValue(currentValue)}</div>
            {subtitle && <div className="text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</div>}
          </div>

          {/* Previous Period */}
          {previousValue !== null && previousValue !== undefined && (
            <div className="pt-1.5 border-t">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Previous Period</span>
                <span className="font-medium">{formatValue(previousValue)}</span>
              </div>
              
              {/* Change Indicator */}
              <div className={cn(
                "flex items-center gap-1 mt-1 text-xs font-medium",
                isPositive && "text-green-600",
                isNegative && "text-red-600",
                isNeutral && "text-muted-foreground"
              )}>
                {isPositive && <TrendingUp className="h-3 w-3" />}
                {isNegative && <TrendingDown className="h-3 w-3" />}
                {isNeutral && <Minus className="h-3 w-3" />}
                <span>
                  {isPositive ? '+' : ''}{change.toFixed(2)}%
                  {format === 'currency' && ` (${changeAbs > 0 ? '+' : ''}${formatValue(changeAbs)})`}
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ComparisonCard;
